import type {
  Agency,
  AuditEntry,
  CreateUserInput,
  CrypticGreeting,
  CrypticResponse,
  PNRSummary,
  MfaChallenge,
  MfaSetup,
  MfaStatus,
  Session,
  User,
  UpdateUserInput,
  UserListResponse,
  UserResponse,
} from './types';

const BASE_URL = '/api/v1';

/** Raised for every non-2xx response so callers can branch on code/fields. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

export const SESSION_EXPIRED_EVENT = 'sabre:session-expired';

/**
 * The access token lives in memory only. A page reload recovers the session
 * from the httpOnly refresh cookie instead, so no token is ever written to
 * localStorage where a script injection could read it.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Attach the bearer token and retry once through /auth/refresh on a 401. */
  authenticated?: boolean;
  signal?: AbortSignal;
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'request_failed';
  let message = 'Something went wrong. Please try again.';
  let fields: Record<string, string> = {};
  try {
    const payload = await response.json();
    if (payload?.error) {
      code = payload.error.code ?? code;
      message = payload.error.message ?? message;
      fields = payload.error.fields ?? {};
    }
  } catch {
    // A non-JSON body (proxy error page, gateway timeout) keeps the defaults.
  }
  return new ApiError(response.status, code, message, fields);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.authenticated && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    signal: options.signal,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await send(path, options);
  } catch {
    throw new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection and try again.');
  }

  // A short-lived access token expires quietly; renew it once and replay.
  if (response.status === 401 && options.authenticated) {
    const renewed = await tryRefresh();
    if (renewed) {
      try {
        response = await send(path, options);
      } catch {
        throw new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection and try again.');
      }
    } else {
      setAccessToken(null);
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      throw await parseError(response);
    }
  }

  if (!response.ok) {
    throw await parseError(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

let refreshInFlight: Promise<Session> | null = null;

/**
 * Refreshes the session, collapsing concurrent callers onto one request.
 *
 * The refresh cookie is single use: the host rotates it and drops the old one
 * on every refresh, so a second request carrying the token the first already
 * spent is a token that no longer exists. The host answers 401 and clears the
 * cookie with it, which ends the session outright -- the very thing a refresh
 * is meant to avoid. Two callers is not a hypothetical: React mounts the tree
 * twice in development, a renewal can fall due while a 401 is being retried,
 * and both are one spent token away from signing the agent out mid-trip.
 *
 * So there is one request, and everyone waiting gets its answer. The slot is
 * released once it settles, so the next expiry starts a fresh attempt.
 */
export function refreshSession(): Promise<Session> {
  if (!refreshInFlight) {
    refreshInFlight = request<Session>('/auth/refresh', { method: 'POST' }).finally(() => {
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    });
  }
  return refreshInFlight;
}

/** Renews the access token for a replayed request; false if the session is over. */
async function tryRefresh(): Promise<boolean> {
  try {
    const session = await refreshSession();
    setAccessToken(session.accessToken);
    return true;
  } catch {
    return false;
  }
}

// ----------------------------------------------------------------- auth

export function login(userId: string, password: string, pcc: string): Promise<Session | MfaChallenge> {
  return request<Session | MfaChallenge>('/auth/login', { method: 'POST', body: { userId, password, pcc } });
}

/** The second sign-in step: an authenticator code or a backup code. */
export function loginMfa(mfaToken: string, code: string): Promise<Session> {
  return request<Session>('/auth/login/mfa', { method: 'POST', body: { mfaToken, code } });
}

export function isMfaChallenge(value: Session | MfaChallenge): value is MfaChallenge {
  return 'mfaToken' in value;
}

/** First-time setup during sign-in: the QR code for the setup token. */
export function loginMfaSetup(mfaToken: string): Promise<MfaSetup> {
  return request<MfaSetup>('/auth/login/mfa/setup', { method: 'POST', body: { mfaToken } });
}

/** Confirms the first code; signs in and returns the backup codes too. */
export function loginMfaEnable(mfaToken: string, code: string): Promise<Session & { recoveryCodes: string[] }> {
  return request('/auth/login/mfa/enable', { method: 'POST', body: { mfaToken, code } });
}

// ------------------------------------------------- two-step verification

export function fetchMfaStatus(): Promise<MfaStatus> {
  return request<MfaStatus>('/auth/mfa', { authenticated: true });
}

export function startMfaSetup(password: string): Promise<MfaSetup> {
  return request<MfaSetup>('/auth/mfa/setup', { method: 'POST', authenticated: true, body: { password } });
}

export function enableMfa(code: string): Promise<{ recoveryCodes: string[]; user: User }> {
  return request('/auth/mfa/enable', { method: 'POST', authenticated: true, body: { code } });
}

export function disableMfa(password: string, code: string): Promise<{ user: User }> {
  return request('/auth/mfa/disable', { method: 'POST', authenticated: true, body: { password, code } });
}

export function newRecoveryCodes(code: string): Promise<{ recoveryCodes: string[] }> {
  return request('/auth/mfa/recovery-codes', { method: 'POST', authenticated: true, body: { code } });
}

/** Admin: turn two-step verification off for a user who lost their phone. */
export function resetUserMfa(id: string): Promise<UserResponse> {
  return request<UserResponse>(`/users/${id}/mfa`, { method: 'DELETE', authenticated: true });
}

export function logout(): Promise<{ message: string }> {
  return request<{ message: string }>('/auth/logout', { method: 'POST' });
}

export function fetchSession(): Promise<{ user: UserResponse['user']; pcc?: string; mustChangePassword: boolean }> {
  return request('/auth/session', { authenticated: true });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<Session> {
  return request<Session>('/auth/password', {
    method: 'POST',
    authenticated: true,
    body: { currentPassword, newPassword },
  });
}

// ---------------------------------------------------------------- users

export interface UserQuery {
  search?: string;
  role?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export function listUsers(query: UserQuery = {}, signal?: AbortSignal): Promise<UserListResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return request<UserListResponse>(`/users${qs ? `?${qs}` : ''}`, { authenticated: true, signal });
}

export function createUser(input: CreateUserInput): Promise<UserResponse> {
  return request<UserResponse>('/users', { method: 'POST', authenticated: true, body: input });
}

export function updateUser(id: string, input: UpdateUserInput): Promise<UserResponse> {
  return request<UserResponse>(`/users/${id}`, { method: 'PATCH', authenticated: true, body: input });
}

export function deleteUser(id: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/users/${id}`, { method: 'DELETE', authenticated: true });
}

export function resetUserPassword(
  id: string,
  password?: string,
  mustChangePassword = true,
): Promise<UserResponse> {
  return request<UserResponse>(`/users/${id}/password`, {
    method: 'POST',
    authenticated: true,
    body: { password: password ?? '', mustChangePassword },
  });
}

export function listAudit(limit = 100): Promise<{ entries: AuditEntry[] }> {
  return request<{ entries: AuditEntry[] }>(`/audit?limit=${limit}`, { authenticated: true });
}

export function listAgencies(): Promise<{ agencies: Agency[] }> {
  return request<{ agencies: Agency[] }>('/admin/agencies', { authenticated: true });
}

export function saveAgency(agency: Agency): Promise<{ agency: Agency }> {
  return request<{ agency: Agency }>(`/admin/agencies/${encodeURIComponent(agency.pcc)}`, {
    method: 'PUT',
    authenticated: true,
    body: { name: agency.name, devices: agency.devices },
  });
}

export function listPNRs(query = '', pcc = ''): Promise<{ pnrs: PNRSummary[] }> {
  const params = new URLSearchParams({ q: query, pcc });
  return request<{ pnrs: PNRSummary[] }>(`/pnrs?${params}`, { authenticated: true });
}

// ------------------------------------------------- cryptic (host) workspace

export function crypticGreeting(area: string): Promise<CrypticGreeting> {
  return request<CrypticGreeting>(`/cryptic?area=${encodeURIComponent(area)}`, { authenticated: true });
}

export function sendCrypticEntry(entry: string, area: string): Promise<CrypticResponse> {
  return request<CrypticResponse>('/cryptic', { method: 'POST', authenticated: true, body: { entry, area } });
}

// ------------------------------------------------------ reference data admin

export interface RefList<T> {
  items: T[];
  total: number;
}

/** One page of a reference table, filtered by the search box. */
export function listReference<T>(
  resource: string,
  query: { search?: string; limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<RefList<T>> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return request<RefList<T>>(`/admin/${resource}${qs ? `?${qs}` : ''}`, { authenticated: true, signal });
}

export function createReference<T>(resource: string, body: unknown): Promise<{ item: T }> {
  return request<{ item: T }>(`/admin/${resource}`, { method: 'POST', authenticated: true, body });
}

/** path is the item's address under the resource: a code, an id or a flight key. */
export function updateReference<T>(resource: string, path: string, body: unknown): Promise<{ item: T }> {
  return request<{ item: T }>(`/admin/${resource}/${path}`, { method: 'PUT', authenticated: true, body });
}

export function deleteReference(resource: string, path: string): Promise<void> {
  return request<void>(`/admin/${resource}/${path}`, { method: 'DELETE', authenticated: true });
}
